import { useConfig } from './queries';

const DEFAULT_ERP = 'https://erp.eleva.io';
const trim = (u: string) => u.replace(/\/+$/, '');

/** Medianoche de hoy en Europe/Madrid, en UTC (p. ej. 16 oct → 2026-10-15T22:00:00.000Z en horario de verano) */
export function madridMidnightUtc(now = new Date()): Date {
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(now)
    .split('-')
    .map(Number) as [number, number, number];
  const utcMidnight = Date.UTC(ymd[0], ymd[1] - 1, ymd[2]);
  // Desfase de Madrid en ese instante («GMT+02:00»)
  const off = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Madrid', timeZoneName: 'longOffset' })
    .formatToParts(new Date(utcMidnight))
    .find((p) => p.type === 'timeZoneName')!.value;
  const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(off);
  const minutes = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : 0;
  return new Date(utcMidnight - minutes * 60_000);
}

/** Enlaces a la web del ERP (mismo patrón que las URLs de erp.eleva.io) */
export function erpLinks(base = DEFAULT_ERP) {
  const b = trim(base);
  return {
    community: (communityId: string) => `${b}/horizontal/communities/${communityId}`,
    meeting: (communityId: string, meetingId: string) =>
      `${b}/horizontal/communities/${communityId}/meetings/${meetingId}`,
    ticket: (ticketId: string) => `${b}/tickets/${ticketId}`,
    /** Juntas con el acta pendiente (misma definición que J1): estados 3/4/6/12 y fecha hasta ayer incluido */
    pendingMinutes: (now = new Date()) => {
      const filters = {
        date: { to: new Date(madridMidnightUtc(now).getTime() - 1).toISOString() },
        status: ['scheduled', 'in_progress', 'post_execution', 'awaiting_minutes_approval'],
      };
      return `${b}/horizontal/meetings?meetings-v1_filters=${encodeURIComponent(JSON.stringify(filters))}`;
    },
    /** Enlace al objeto de una alerta (ticket o junta), o a la comunidad si no hay página propia */
    object: (a: {
      objectType: string;
      objectId: string;
      communityId: string;
      manual?: { erpUrl: string | null };
    }) =>
      a.objectType === 'manual'
        ? (a.manual?.erpUrl ?? `${b}/horizontal/communities/${a.communityId}`)
        : a.objectType === 'ticket'
          ? `${b}/tickets/${a.objectId}`
          : a.objectType === 'meeting'
            ? `${b}/horizontal/communities/${a.communityId}/meetings/${a.objectId}`
            : `${b}/horizontal/communities/${a.communityId}`,
  };
}

export const useErpLinks = () => erpLinks(useConfig().data?.erpUrl ?? DEFAULT_ERP);
